// The drawing styles in the browser: the app's own, then the ones people wrote (kept in the library). The last one
// painted with is remembered in this browser and picked first next time.
import { getLibrary, setLibrary, useLibrary } from "../../library/store";
import { DEFAULT_STYLE, PRESET_STYLES, type PaintStyle } from "../../model/styles";
import { api } from "../api";

const KEY = "paint-style";

export function useStyles(): PaintStyle[] {
  const lib = useLibrary();
  return [...PRESET_STYLES, ...(lib.styles ?? [])];
}

export function lastStyleId(): string {
  try { return localStorage.getItem(KEY) ?? DEFAULT_STYLE.id; } catch { return DEFAULT_STYLE.id; }
}
export function rememberStyle(id: string) {
  try { localStorage.setItem(KEY, id); } catch {}
}

// The style with this id, else the default (a custom style that was deleted, say).
export const styleById = (all: PaintStyle[], id: string) => all.find(s => s.id === id) ?? DEFAULT_STYLE;

// A custom style's face set, drawn in the background after the style is saved (a minute or so). If the style's words
// change meanwhile, the faces are for the old words, so they're dropped.
export async function makeStyleFaces(style: PaintStyle) {
  try {
    const { sprites } = await api.styleFaces(style.id, style.text);
    const now = (getLibrary().styles ?? []).find(s => s.id === style.id);
    if (now && now.text === style.text && Object.keys(sprites).length) await saveCustomStyle({ ...now, faces: sprites });
  } catch (e) { console.error("Couldn't make the style's faces:", e); }
}

// Add, change or (with `remove`) delete a style of your own. The library shows it straight away, then it's saved.
export async function saveCustomStyle(style: PaintStyle, remove = false) {
  const mine = getLibrary().styles ?? [];
  const next = remove ? mine.filter(s => s.id !== style.id) : mine.some(s => s.id === style.id) ? mine.map(s => (s.id === style.id ? style : s)) : [...mine, style];
  setLibrary({ ...getLibrary(), styles: next });
  await api.saveStyles(next);
}
